/**
 * Plain-language descriptions for blood test markers.
 * Keyed by normalized marker name (from normalize-name-map.json).
 * Each entry: { description, high, low }
 */

const markerDescriptions = {
  // --- Blood Sugar / Metabolic ---
  "Glucose": {
    description: "Measures blood sugar available for energy.",
    high: "Body can't move sugar into cells fast enough, stressing insulin production and adrenal function.",
    low: "Not enough blood sugar for the brain and muscles to function properly."
  },
  "Hemoglobin A1c": {
    description: "Average blood sugar over the past 2-3 months.",
    high: "Chronically elevated blood sugar is damaging blood vessels and increasing diabetes risk.",
    low: "Unusually low average sugar may indicate hypoglycemia episodes or red blood cell turnover issues."
  },
  "Insulin": {
    description: "Hormone that moves sugar from blood into cells.",
    high: "Body is overproducing insulin to force sugar into resistant cells — early sign of metabolic dysfunction.",
    low: "Pancreas isn't producing enough insulin, or cells are highly sensitive (context-dependent)."
  },

  // --- Kidney ---
  "BUN": {
    description: "Waste product from protein breakdown filtered by kidneys.",
    high: "Kidneys may be struggling to filter waste, or protein intake/breakdown is excessive.",
    low: "May indicate low protein intake, liver issues, or overhydration."
  },
  "Creatinine": {
    description: "Muscle waste product filtered by kidneys — reflects kidney filtration rate.",
    high: "Kidneys aren't clearing waste efficiently, suggesting reduced kidney function.",
    low: "May reflect low muscle mass or very efficient kidney filtration."
  },
  "BUN/Creatinine Ratio": {
    description: "Compares two kidney waste markers to help pinpoint the cause of kidney stress.",
    high: "Suggests dehydration, high protein diet, or reduced blood flow to the kidneys.",
    low: "May indicate liver disease or low protein intake."
  },
  "eGFR": {
    description: "Estimated rate at which kidneys filter blood — the primary kidney function score.",
    high: null,
    low: "Kidneys are filtering blood too slowly, indicating reduced kidney function."
  },
  "Uric Acid": {
    description: "Waste product from purine breakdown — cleared by kidneys.",
    high: "Excess uric acid can crystallize in joints (gout) and damage kidneys.",
    low: "May indicate oxidative stress, low purine intake, or kidney wasting."
  },

  // --- Liver ---
  "ALT": {
    description: "Liver-specific enzyme released when liver cells are damaged.",
    high: "Liver cells are under stress or being damaged — common with toxin exposure, fatty liver, or infection.",
    low: "May indicate vitamin B6 deficiency, as B6 is needed to produce this enzyme."
  },
  "AST": {
    description: "Enzyme found in liver, heart, and muscle — released during cell damage.",
    high: "Cells are being damaged — could be liver, heart, or muscle depending on context.",
    low: "Generally not clinically significant on its own."
  },
  "Alkaline Phosphatase": {
    description: "Enzyme from liver and bone involved in breaking down proteins.",
    high: "May indicate bile duct obstruction, liver disease, or increased bone turnover.",
    low: "Can signal zinc or magnesium deficiency, or low thyroid function."
  },
  "GGT": {
    description: "Liver enzyme sensitive to alcohol use and bile duct problems.",
    high: "Liver is under oxidative stress — commonly from alcohol, medications, or bile duct issues.",
    low: "Generally not clinically significant."
  },
  "Bilirubin (Total)": {
    description: "Yellow pigment from red blood cell breakdown, processed by the liver.",
    high: "Liver can't process bilirubin fast enough, or red blood cells are breaking down too quickly.",
    low: "Generally not clinically significant."
  },
  "Bilirubin (Direct)": {
    description: "Bilirubin already processed by the liver — ready for excretion.",
    high: "Bile flow is obstructed, preventing the liver from excreting processed bilirubin.",
    low: "Generally not clinically significant."
  },
  "LDH": {
    description: "Enzyme released when cells are damaged — found in most tissues.",
    high: "Tissue damage is occurring somewhere — could be liver, blood cells, heart, or muscle.",
    low: "Generally not clinically significant."
  },

  // --- Protein / Digestion ---
  "Protein (Total)": {
    description: "Total protein in blood — reflects nutrition, liver function, and immune status.",
    high: "May indicate chronic inflammation, dehydration, or immune system overactivation.",
    low: "Body isn't making or absorbing enough protein — liver, gut, or nutritional issue."
  },
  "Albumin": {
    description: "Most abundant blood protein — made by the liver, carries nutrients and maintains fluid balance.",
    high: "Usually indicates dehydration rather than overproduction.",
    low: "Liver isn't producing enough, or protein is being lost through kidneys or gut inflammation."
  },
  "Globulin (Total)": {
    description: "Immune and transport proteins in the blood.",
    high: "Immune system is overactivated — chronic infection, inflammation, or autoimmune activity.",
    low: "Immune system may be underperforming, or liver isn't producing enough."
  },
  "A/G Ratio": {
    description: "Ratio of albumin to globulin — reflects the balance between nutrition and immune activity.",
    high: "May indicate underactive immune response or genetic variants.",
    low: "Immune proteins are elevated relative to albumin — suggests inflammation or liver dysfunction."
  },

  // --- Electrolytes ---
  "Sodium": {
    description: "Electrolyte that controls fluid balance and nerve signaling.",
    high: "Body is retaining too much sodium or losing too much water — strains the heart and kidneys.",
    low: "Diluted sodium levels can cause brain swelling, confusion, and muscle weakness."
  },
  "Potassium": {
    description: "Electrolyte critical for heart rhythm, muscle contraction, and nerve function.",
    high: "Dangerous for the heart — can cause irregular heartbeat or cardiac arrest.",
    low: "Muscles weaken, cramps increase, and heart rhythm becomes unstable."
  },
  "Chloride": {
    description: "Electrolyte that maintains fluid balance and stomach acid production.",
    high: "May indicate dehydration, kidney problems, or metabolic acidosis.",
    low: "Often accompanies low sodium — may indicate vomiting, overhydration, or adrenal issues."
  },
  "Carbon Dioxide (Total)": {
    description: "Measures bicarbonate — reflects the body's acid-base balance.",
    high: "Blood is too alkaline — may indicate vomiting, overbreathing compensation, or kidney issues.",
    low: "Blood is too acidic — may indicate kidney disease, uncontrolled diabetes, or severe diarrhea."
  },
  "Calcium": {
    description: "Mineral essential for bones, nerve signaling, muscle contraction, and blood clotting.",
    high: "Can indicate overactive parathyroid, excess vitamin D, or cancer-related bone breakdown.",
    low: "Nerves and muscles become hyperexcitable — causes tingling, cramps, and potentially seizures."
  },
  "Phosphorus": {
    description: "Mineral critical for bone strength, energy production, and cell repair.",
    high: "Often indicates kidney dysfunction — excess phosphorus pulls calcium from bones.",
    low: "Weakens bones and impairs energy production at the cellular level."
  },
  "Magnesium": {
    description: "Mineral involved in 300+ enzyme reactions — critical for muscles, nerves, and heart rhythm.",
    high: "Can slow heart rate and depress nerve function — often from kidney impairment.",
    low: "Triggers muscle cramps, anxiety, irregular heartbeat, and poor sleep."
  },

  // --- Lipids / Cardiovascular ---
  "Cholesterol": {
    description: "Total cholesterol — building block for hormones, cell membranes, and bile.",
    high: "Excess cholesterol increases plaque buildup in arteries, raising heart attack and stroke risk.",
    low: "May impair hormone production and cell membrane integrity."
  },
  "Triglycerides": {
    description: "Fat circulating in the blood — main storage form of energy from food.",
    high: "Excess triglycerides thicken blood and accelerate artery damage — driven by sugar and alcohol.",
    low: "May indicate malnutrition or malabsorption."
  },
  "HDL": {
    description: "Protective cholesterol that removes plaque from arteries.",
    high: "Generally protective — very high levels are being studied for potential inflammation links.",
    low: "Arteries lose their cleanup crew — plaque accumulates faster, increasing heart disease risk."
  },
  "LDL": {
    description: "Cholesterol that delivers fat to tissues — excess deposits in artery walls.",
    high: "Primary driver of atherosclerosis — plaque builds up and narrows arteries.",
    low: "Generally favorable, though very low levels may affect hormone production."
  },
  "VLDL": {
    description: "Carries triglycerides from the liver to tissues — converts to LDL.",
    high: "Liver is exporting too much fat — increases artery-damaging particle count.",
    low: "Generally not clinically significant."
  },
  "Chol/HDL Ratio": {
    description: "Ratio of total cholesterol to protective HDL — key cardiovascular risk indicator.",
    high: "More damaging cholesterol relative to protective — elevated heart disease risk.",
    low: "Favorable balance of protective cholesterol."
  },
  "Apolipoprotein B": {
    description: "One particle per LDL/VLDL — directly counts artery-damaging particles.",
    high: "High particle count means more cholesterol getting deposited in artery walls.",
    low: "Low atherogenic particle count — generally favorable."
  },
  "CRP (High Sensitivity)": {
    description: "High-sensitivity measure of low-grade inflammation — used for cardiovascular risk assessment.",
    high: "Chronic low-grade inflammation is present — significantly increases heart attack and stroke risk.",
    low: "Low inflammation levels — generally favorable."
  },
  "Homocysteine": {
    description: "Amino acid byproduct — elevated levels damage blood vessel walls.",
    high: "Impaired methylation is allowing homocysteine to accumulate, damaging arteries and increasing clot risk.",
    low: "Generally favorable."
  },
  "Estimated CHD Risk": {
    description: "Calculated estimate of coronary heart disease risk based on lipid values.",
    high: "Lipid profile indicates elevated risk for coronary heart disease.",
    low: "Lipid profile suggests lower coronary heart disease risk."
  },

  // --- Iron Panel ---
  "Iron": {
    description: "Mineral that carries oxygen in red blood cells and supports energy production.",
    high: "Excess iron is toxic — damages liver, heart, and pancreas through oxidative stress.",
    low: "Tissues are oxygen-starved — causes fatigue, brain fog, and eventual anemia."
  },
  "Ferritin": {
    description: "Iron storage protein — reflects total body iron reserves and inflammation.",
    high: "Either iron overload or active inflammation is driving storage up — both damage organs.",
    low: "Iron reserves are depleted — anemia will develop if not corrected."
  },
  "TIBC": {
    description: "Measures how much iron the blood can carry — reflects iron transport capacity.",
    high: "Body is ramping up iron transport because stores are low — sign of iron deficiency.",
    low: "May indicate iron overload, chronic inflammation, or liver disease."
  },
  "UIBC": {
    description: "Unsaturated iron-binding capacity — how much room is left on iron transport proteins.",
    high: "Transport proteins are mostly empty — body needs more iron.",
    low: "Transport proteins are saturated — may indicate iron overload."
  },
  "Iron Saturation": {
    description: "Percentage of iron transport proteins that are carrying iron.",
    high: "Iron transport is saturated — excess iron may be depositing in organs.",
    low: "Not enough iron is being transported — tissues are undersupplied."
  },
  "Transferrin": {
    description: "Protein that carries iron through the blood to where it's needed.",
    high: "Body is producing more carriers because iron is scarce.",
    low: "May indicate inflammation, liver disease, or iron overload suppressing production."
  },

  // --- Red Blood Cells ---
  "RBC": {
    description: "Red blood cell count — these cells carry oxygen to every tissue.",
    high: "Body is overproducing red cells — possibly from dehydration, low oxygen, or bone marrow issues.",
    low: "Fewer oxygen carriers — tissues receive less oxygen, causing fatigue and weakness."
  },
  "Hemoglobin": {
    description: "Oxygen-carrying protein inside red blood cells.",
    high: "Blood is thicker with more oxygen carriers — increases clot risk.",
    low: "Anemia — not enough hemoglobin to deliver adequate oxygen to tissues."
  },
  "Hematocrit": {
    description: "Percentage of blood volume occupied by red blood cells.",
    high: "Blood is too thick — increases risk of clots, stroke, and heart strain.",
    low: "Too few red cells relative to plasma — indicates anemia or fluid overload."
  },
  "MCV": {
    description: "Average size of red blood cells — helps classify types of anemia.",
    high: "Red cells are too large — typically from B12 or folate deficiency.",
    low: "Red cells are too small — typically from iron deficiency or chronic disease."
  },
  "MCH": {
    description: "Average amount of hemoglobin per red blood cell.",
    high: "Cells are packed with hemoglobin — usually accompanies large cells (B12/folate issue).",
    low: "Cells carry less oxygen each — iron deficiency is the most common cause."
  },
  "MCHC": {
    description: "Concentration of hemoglobin within red blood cells.",
    high: "Cells are overly concentrated — may indicate spherocytosis or dehydrated red cells.",
    low: "Cells are dilute in hemoglobin — iron deficiency is the most common cause."
  },
  "RDW": {
    description: "Variation in red blood cell size — indicates how uniform the cells are.",
    high: "Red cells vary widely in size — suggests mixed nutritional deficiencies or bone marrow stress.",
    low: "Cells are very uniform in size — generally normal."
  },
  "Reticulocyte Count": {
    description: "Young red blood cells just released from bone marrow — shows production rate.",
    high: "Bone marrow is working overtime to replace lost or destroyed red cells.",
    low: "Bone marrow isn't producing enough new red cells — may indicate nutritional deficiency or marrow suppression."
  },

  // --- White Blood Cells / Immune ---
  "WBC": {
    description: "Total white blood cell count — the immune system's first responders.",
    high: "Immune system is actively fighting something — infection, inflammation, or stress response.",
    low: "Immune defenses are weakened — increased vulnerability to infections."
  },
  "Neutrophils": {
    description: "Percentage of white cells that are neutrophils — bacterial first responders. Reflects the relative share, not total count.",
    high: "Disproportionately high neutrophil share — bacterial infection, acute stress, or inflammation shifting the balance.",
    low: "Low neutrophil proportion — viral infection or immune suppression shifting the differential."
  },
  "Neutrophils (Absolute)": {
    description: "Total neutrophil count per µL of blood — measures actual bacterial defense capacity regardless of other white cell levels.",
    high: "Body is actively fighting bacterial infection or acute inflammation.",
    low: "Dangerously low bacterial defense — high infection risk."
  },
  "Lymphocytes": {
    description: "Percentage of white cells that are lymphocytes — viral and adaptive immune cells. Reflects relative share, not total count.",
    high: "Lymphocytes dominate the differential — viral infection or chronic immune activation shifting the balance.",
    low: "Low lymphocyte proportion — may reflect neutrophil dominance from bacterial infection or stress."
  },
  "Lymphocytes (Absolute)": {
    description: "Total lymphocyte count per µL of blood — measures actual viral and adaptive immune capacity.",
    high: "Active viral infection or chronic immune stimulation.",
    low: "Adaptive immunity is suppressed — reduced ability to fight viruses and mount immune memory."
  },
  "Monocytes": {
    description: "Percentage of white cells that are monocytes — debris cleanup cells. Reflects relative share, not total count.",
    high: "Higher monocyte proportion — chronic inflammation, infection recovery, or autoimmune activation.",
    low: "Generally not clinically significant on its own."
  },
  "Monocytes (Absolute)": {
    description: "Total monocyte count per µL of blood — measures actual debris cleanup and immune coordination capacity.",
    high: "Chronic inflammation or recovering from infection.",
    low: "Generally not clinically significant on its own."
  },
  "Eosinophils": {
    description: "Percentage of white cells that are eosinophils — allergy and parasite defense. Reflects relative share, not total count.",
    high: "High eosinophil proportion — allergic reaction, parasitic infection, or eosinophilic condition.",
    low: "Generally not clinically significant."
  },
  "Eosinophils (Absolute)": {
    description: "Total eosinophil count per µL of blood — measures actual allergy and parasite defense capacity.",
    high: "Active allergic response or parasitic infection.",
    low: "Generally not clinically significant."
  },
  "Basophils": {
    description: "Percentage of white cells that are basophils — rarest white cell, involved in allergic responses. Reflects relative share, not total count.",
    high: "High basophil proportion — may indicate allergic reaction, chronic inflammation, or rarely myeloproliferative disorders.",
    low: "Generally not clinically significant."
  },
  "Basophils (Absolute)": {
    description: "Total basophil count per µL of blood — measures actual allergic and inflammatory response capacity.",
    high: "May indicate allergic or inflammatory activation.",
    low: "Generally not clinically significant."
  },
  "Immature Granulocytes": {
    description: "Young immune cells released early from bone marrow — a stress signal.",
    high: "Bone marrow is releasing immature cells to meet urgent immune demand — sign of severe infection or stress.",
    low: "Normal — these should typically be at zero."
  },
  "Immature Grans (Absolute)": {
    description: "Absolute count of immature granulocytes released early from bone marrow.",
    high: "Severe infection or inflammation is forcing bone marrow to release unfinished immune cells.",
    low: "Normal — these should typically be near zero."
  },
  "Platelets": {
    description: "Cell fragments that form blood clots to stop bleeding.",
    high: "Increased clotting risk — may indicate inflammation, iron deficiency, or bone marrow overactivity.",
    low: "Bleeding risk increases — may indicate bone marrow suppression, autoimmune destruction, or viral infection."
  },

  // --- Thyroid ---
  "TSH": {
    description: "Brain signal telling the thyroid how hard to work — the primary thyroid screening test.",
    high: "Brain is shouting at an underperforming thyroid — hypothyroidism (slow metabolism, fatigue, weight gain).",
    low: "Thyroid is overproducing on its own — hyperthyroidism (anxiety, weight loss, rapid heart rate)."
  },
  "T4": {
    description: "Main thyroid hormone in storage form — converted to active T3 in tissues.",
    high: "Thyroid is overproducing — may cause anxiety, tremors, and weight loss.",
    low: "Thyroid output is insufficient — leads to fatigue, cold intolerance, and sluggish metabolism."
  },
  "T4 (free)": {
    description: "Unbound T4 available for conversion to active thyroid hormone.",
    high: "Excess available thyroid hormone — overactive thyroid or excessive medication.",
    low: "Not enough thyroid hormone available — tissues can't maintain metabolic rate."
  },
  "T3": {
    description: "Active thyroid hormone that drives metabolism in every cell.",
    high: "Metabolism is running too fast — weight loss, anxiety, heart palpitations.",
    low: "Cells aren't getting enough metabolic signal — fatigue, brain fog, cold intolerance."
  },
  "T3 (free)": {
    description: "Unbound active thyroid hormone — the form cells actually use.",
    high: "Too much active thyroid signal reaching cells — hypermetabolic state.",
    low: "Cells are starved of active thyroid hormone despite possibly normal T4 — conversion problem."
  },
  "Reverse T3": {
    description: "Inactive form of T3 — blocks active T3 from working when the body is under stress.",
    high: "Body is converting T4 to inactive T3 instead of active — stress, illness, or inflammation is shutting down metabolism.",
    low: "Generally not clinically significant."
  },
  "T3 Uptake": {
    description: "Indirect measure of thyroid-binding proteins — helps interpret T4 levels.",
    high: "Fewer binding proteins — more free hormone available (or low protein states).",
    low: "More binding proteins — less free hormone available (estrogen, pregnancy, liver disease)."
  },
  "FTI (Free Thyroxine Index)": {
    description: "Calculated estimate of free T4 — corrects for binding protein variations.",
    high: "Suggests excess available thyroid hormone.",
    low: "Suggests insufficient available thyroid hormone."
  },
  "Thyroid Peroxidase (TPO) Ab": {
    description: "Antibodies attacking the thyroid enzyme that makes hormones — autoimmune marker.",
    high: "Immune system is attacking the thyroid — Hashimoto's thyroiditis or Graves' disease.",
    low: "No significant autoimmune thyroid activity."
  },
  "Thyroglobulin Ab": {
    description: "Antibodies against thyroglobulin — another autoimmune thyroid marker.",
    high: "Immune system is targeting thyroid protein — supports autoimmune thyroid diagnosis.",
    low: "No significant autoimmune thyroid activity."
  },
  "Thyroxine-binding Globulin (TBG)": {
    description: "Protein that carries thyroid hormones in the blood.",
    high: "More hormone is bound and unavailable — effectively reduces free thyroid hormone.",
    low: "Less binding means more free hormone — can mimic hyperthyroidism."
  },
  "SHBG": {
    description: "Protein that binds sex hormones — controls how much testosterone and estrogen are available.",
    high: "More hormones are locked up and unavailable — can cause symptoms of low testosterone/estrogen.",
    low: "More free hormones circulating — may drive excess androgen or estrogen effects."
  },

  // --- Vitamins ---
  "Vitamin D, 25-Hydroxy": {
    description: "Storage form of vitamin D — essential for bones, immune function, and mood.",
    high: "Toxicity risk — excess vitamin D causes dangerous calcium buildup.",
    low: "Bones weaken, immune function drops, mood worsens, and inflammation increases."
  },
  "1,25-Dihydroxy Vitamin D": {
    description: "Active form of vitamin D — regulates calcium absorption and immune responses.",
    high: "Body is overactivating vitamin D — may indicate granulomatous disease or excess parathyroid activity.",
    low: "Active vitamin D is insufficient — impairs calcium absorption and immune regulation."
  },
  "Vitamin B12": {
    description: "Essential vitamin for nerve function, DNA synthesis, and red blood cell production.",
    high: "Rarely harmful from food — very high levels may indicate liver release or myeloproliferative disorder.",
    low: "Nerves deteriorate, red cells enlarge, and cognitive function declines."
  },
  "Folate": {
    description: "B vitamin essential for DNA synthesis, cell division, and methylation.",
    high: "Rarely a problem from food — may mask B12 deficiency.",
    low: "Impairs cell division and DNA repair — causes macrocytic anemia and increases homocysteine."
  },

  // --- Hormones ---
  "Testosterone": {
    description: "Primary male sex hormone — drives muscle, bone density, mood, and libido.",
    high: "Excess testosterone can cause aggression, acne, and in women, virilization.",
    low: "Muscle loss, fatigue, low libido, depression, and bone density decline."
  },
  "Testosterone (free)": {
    description: "Unbound testosterone available for tissues to use.",
    high: "Excess available testosterone — may drive acne, hair loss, or mood changes.",
    low: "Not enough active testosterone reaching tissues despite possibly normal total levels."
  },
  "PSA": {
    description: "Prostate-specific antigen — screening marker for prostate health.",
    high: "Prostate is inflamed, enlarged, or possibly harboring cancer — needs further evaluation.",
    low: "Generally favorable for prostate health."
  },

  // --- Hormones (additional) ---
  "Cortisol": {
    description: "Primary stress hormone — regulates metabolism, immune response, and blood pressure.",
    high: "Chronic stress is keeping cortisol elevated, breaking down muscle, raising blood sugar, and suppressing immunity.",
    low: "Adrenal glands can't produce enough cortisol — causes fatigue, low blood pressure, and poor stress tolerance."
  },
  "DHEA-Sulfate": {
    description: "Adrenal hormone precursor to testosterone and estrogen — declines with age.",
    high: "Adrenals are overproducing — may indicate adrenal tumor or congenital adrenal hyperplasia.",
    low: "Adrenal output is declining — associated with aging, fatigue, reduced libido, and weakened immune function."
  },
  "Estradiol": {
    description: "Primary estrogen hormone — critical for bone health, brain function, and reproductive health.",
    high: "Excess estrogen can drive weight gain, mood swings, and increase risk of hormone-sensitive conditions.",
    low: "Estrogen deficiency leads to bone loss, hot flashes, brain fog, and cardiovascular risk."
  },
  "LH": {
    description: "Luteinizing hormone — signals the gonads to produce sex hormones.",
    high: "Gonads are underperforming, so the brain is sending stronger signals — may indicate menopause or primary hypogonadism.",
    low: "Brain isn't signaling the gonads adequately — may indicate pituitary dysfunction or hypothalamic suppression."
  },
  "FSH": {
    description: "Follicle-stimulating hormone — drives egg and sperm production.",
    high: "Gonads are declining, so the brain is overdriving FSH — seen in menopause and primary gonadal failure.",
    low: "Pituitary isn't stimulating the gonads — may indicate central hypogonadism or hormonal suppression."
  },
  "Progesterone": {
    description: "Hormone essential for menstrual cycle regulation, pregnancy support, and calming neurotransmitter activity.",
    high: "May indicate pregnancy, ovarian cysts, or adrenal overproduction.",
    low: "Insufficient progesterone disrupts menstrual cycles, impairs fertility, and worsens anxiety and sleep."
  },
  "Estrogen (Total)": {
    description: "Combined measure of all estrogen forms — reflects overall estrogenic activity.",
    high: "Excess total estrogens may increase risk of hormone-sensitive conditions and cause fluid retention.",
    low: "Low total estrogens contribute to bone loss, cardiovascular risk, and menopausal symptoms."
  },
  "Insulin-Like Growth Factor I": {
    description: "Growth mediator produced by the liver in response to growth hormone — reflects GH activity.",
    high: "Excess growth signaling — may indicate acromegaly or excessive growth hormone production.",
    low: "Growth hormone axis is underperforming — associated with aging, poor recovery, and reduced muscle mass."
  },

  // --- Cardiovascular (additional) ---
  "Non-HDL Cholesterol": {
    description: "All cholesterol minus HDL — captures total atherogenic cholesterol burden.",
    high: "High atherogenic cholesterol load is accelerating plaque buildup in arteries.",
    low: "Low atherogenic burden — generally favorable for cardiovascular health."
  },
  "D-Dimer": {
    description: "Protein fragment from blood clot breakdown — indicates recent or active clotting.",
    high: "Active clotting or clot breakdown is occurring — may indicate DVT, PE, or systemic inflammation.",
    low: "Generally not clinically significant — indicates no active clot formation."
  },
  "Lipoprotein (a)": {
    description: "Genetically determined lipoprotein that promotes clotting and plaque — independent cardiac risk factor.",
    high: "Genetically elevated Lp(a) significantly increases heart attack, stroke, and aortic valve disease risk.",
    low: "Low Lp(a) is favorable — reduced genetic cardiovascular risk."
  },
  "Fibrinogen": {
    description: "Clotting protein and inflammation marker — essential for wound healing.",
    high: "Blood is more prone to clotting — active inflammation or cardiovascular risk.",
    low: "Bleeding risk increases due to impaired clot formation."
  },

  // --- Immune (additional) ---
  "Erythrocyte Sedimentation Rate": {
    description: "Measures how fast red blood cells settle — non-specific inflammation marker.",
    high: "Inflammation is present somewhere in the body — infection, autoimmune disease, or tissue damage.",
    low: "Generally not clinically significant on its own."
  },
  "MPV": {
    description: "Mean platelet volume — indicates the average size and activity of platelets.",
    high: "Larger, more reactive platelets — may indicate increased clotting risk or bone marrow compensation.",
    low: "Smaller, less active platelets — may indicate bone marrow suppression or inflammatory conditions."
  },
  "Anion Gap": {
    description: "Calculated difference between measured cations and anions — reflects acid-base balance.",
    high: "Metabolic acidosis is present — may indicate diabetic ketoacidosis, lactic acidosis, or toxin exposure.",
    low: "May indicate low albumin, bromide exposure, or lab error."
  },

  // --- Bone (additional) ---
  "Zinc": {
    description: "Essential trace mineral for immune function, wound healing, and enzyme activity.",
    high: "Zinc excess can suppress copper absorption and impair immune function.",
    low: "Zinc deficiency impairs immunity, slows wound healing, and reduces taste and smell."
  },

  // --- Other ---
  "Creatine Kinase": {
    description: "Enzyme released from damaged muscle, heart, or brain tissue.",
    high: "Muscle or tissue damage is occurring — from intense exercise, injury, or disease.",
    low: "May indicate low muscle mass."
  },
  "Amylase": {
    description: "Enzyme that breaks down starch — produced by pancreas and salivary glands.",
    high: "Pancreas is inflamed or damaged — pancreatitis is the primary concern.",
    low: "May indicate chronic pancreatic damage with reduced enzyme output."
  },
  "Lactic Acid": {
    description: "Byproduct of anaerobic metabolism — rises when tissues lack oxygen.",
    high: "Tissues aren't getting enough oxygen — from intense exercise, shock, or metabolic dysfunction.",
    low: "Generally not clinically significant."
  },

  // --- Hormones (additional, 2026-10) ---
  "Testosterone (bioavailable)": {
    description: "Testosterone that is free or loosely bound to albumin, so tissues can readily use it.",
    high: "More usable testosterone is reaching tissues - may drive acne, oily skin, hair loss, or in women, excess hair growth.",
    low: "Tissues are getting too little usable testosterone - can cause fatigue, low libido, and muscle loss even with normal total levels."
  },
  "Estrone": {
    description: "A weaker estrogen made mostly in body fat - the main estrogen after menopause.",
    high: "Excess estrone may reflect higher body fat, hormone therapy, or PCOS and can add to estrogen-driven symptoms and risks.",
    low: "Low estrone suggests low overall estrogen production, which can affect bones, mood, and menopausal symptoms."
  },
  "Dihydrotestosterone": {
    description: "A potent androgen made from testosterone - drives prostate growth, body hair, and scalp hair loss.",
    high: "Strong androgen activity may contribute to hair loss, acne, prostate enlargement, or in women, excess hair growth.",
    low: "Reduced androgen activity, often from 5-alpha reductase blocking medications or low testosterone."
  },
  "Pregnenolone": {
    description: "Building-block hormone the body converts into cortisol, DHEA, and sex hormones.",
    high: "May indicate an adrenal enzyme block, supplement use, or an adrenal disorder backing up the hormone pathway.",
    low: "Low output of the hormone building block may reflect reduced adrenal or gonadal steroid production."
  },
  "PSA (Free)": {
    description: "The portion of PSA circulating unbound in the blood - used alongside total PSA.",
    high: "A larger free PSA share is more typical of benign prostate enlargement than cancer.",
    low: "A smaller free PSA share alongside elevated total PSA raises concern for prostate cancer and warrants follow-up."
  },
  "PSA (% Free)": {
    description: "Percent of total PSA that is free - helps tell benign enlargement from prostate cancer.",
    high: "A higher free percentage points more toward benign prostate enlargement than cancer.",
    low: "A lower free percentage raises the likelihood of prostate cancer when total PSA is elevated - needs evaluation."
  },
  "hCG": {
    description: "Pregnancy hormone made by the placenta - also a marker for certain tumors.",
    high: "Usually indicates pregnancy; outside pregnancy, it can signal certain testicular, ovarian, or placental tumors.",
    low: "Lower than expected in pregnancy may indicate a miscalculated date, miscarriage risk, or ectopic pregnancy."
  },
  "Leptin": {
    description: "Hormone made by fat cells that tells the brain how much energy is stored.",
    high: "Often reflects higher body fat and leptin resistance, where the brain ignores fullness signals.",
    low: "Very low fat stores or rare genetic deficiency - can drive intense hunger and disrupt menstrual cycles."
  },
  "Prolactin": {
    description: "Pituitary hormone that drives breast milk production and influences reproductive hormones.",
    high: "Can suppress sex hormones, causing irregular periods, low libido, or milky nipple discharge - may indicate a pituitary tumor or medication effect.",
    low: "Usually not a concern; very low levels may reflect reduced pituitary function."
  },
  "Anti-Mullerian Hormone": {
    description: "Hormone from ovarian follicles that reflects the remaining egg supply (ovarian reserve).",
    high: "Many developing follicles - common in PCOS and in younger women with a large egg supply.",
    low: "Reduced ovarian reserve - fewer eggs remaining, as seen with aging or approaching menopause."
  },

  // --- Blood Sugar, Blood Cells, Immune & Toxins ---
  "Estimated Average Glucose (eAG)": {
    description: "Your A1c result translated into an average blood sugar number over the past 2-3 months.",
    high: "Blood sugar has been running high on average, raising the risk of prediabetes, diabetes, and blood vessel damage.",
    low: "Average blood sugar has been unusually low, which may reflect frequent low-sugar episodes or a red blood cell issue skewing A1c."
  },
  "Nucleated RBC": {
    description: "Immature red blood cells that still have a nucleus, reported per 100 white blood cells.",
    high: "Bone marrow is releasing immature red cells early, often from severe anemia, low oxygen, heavy blood loss, or marrow stress.",
    low: null
  },
  "Nucleated RBC (Absolute)": {
    description: "The actual count of immature, nucleus-containing red blood cells circulating in your blood.",
    high: "Immature red cells are spilling into the blood, a sign the bone marrow is under significant stress or the body is short on oxygen.",
    low: null
  },
  "Lipase": {
    description: "Digestive enzyme made by the pancreas that breaks down dietary fat.",
    high: "The pancreas may be inflamed or injured, most often from pancreatitis, gallstones, or heavy alcohol use.",
    low: "May reflect long-term pancreatic damage that reduces enzyme production, such as chronic pancreatitis or cystic fibrosis."
  },
  "Methylmalonic Acid": {
    description: "A byproduct that builds up when your cells do not have enough usable vitamin B12.",
    high: "Cells are short on active vitamin B12, even if blood B12 looks normal, which can affect nerves, energy, and red blood cells.",
    low: null
  },
  "Lead": {
    description: "Measures lead, a toxic metal with no safe level, in your blood.",
    high: "Recent or ongoing lead exposure that can harm the brain, nerves, kidneys, blood, and blood pressure.",
    low: null
  },
  "Mercury": {
    description: "Measures mercury, a toxic metal most often absorbed from fish or workplace exposure.",
    high: "Recent mercury exposure that can affect the nervous system and kidneys if levels are high or sustained.",
    low: null
  },
  "Rheumatoid Factor": {
    description: "An antibody that can target your own tissues, used to help evaluate rheumatoid arthritis.",
    high: "Positive result raises the possibility of rheumatoid arthritis or another autoimmune or chronic inflammatory condition.",
    low: null
  },
  "ANA Screen": {
    description: "Checks for antibodies that target the nucleus of your own cells, a screen for autoimmune disease.",
    high: "Positive result can point toward an autoimmune condition such as lupus, though many healthy people also test positive.",
    low: null
  },
  "ABO Group": {
    description: "Your blood type (A, B, AB, or O), based on markers on your red blood cells.",
    high: null,
    low: null
  },
  "Rh Factor": {
    description: "Shows whether your red blood cells carry the Rh (D) protein, making your type positive or negative.",
    high: null,
    low: null
  },

  // --- Fatty Acids & Lipoprotein Particles ---
  "Omega-3 Total": {
    description: "Combined level of omega-3 fats (EPA, DHA, DPA, ALA) in your blood, reflecting recent intake.",
    high: "Usually reflects heavy fish or fish oil intake; very high levels can thin the blood slightly.",
    low: "Your body is short on anti-inflammatory omega-3 fats that protect the heart, brain, and blood vessels."
  },
  "Omega-6 Total": {
    description: "Combined level of omega-6 fats, mainly linoleic and arachidonic acid, from nuts, seeds, oils, and meat.",
    high: "Reflects a diet rich in vegetable oils, nuts, or meat; matters most when omega-3 levels are low.",
    low: "May indicate very low fat intake or poor fat absorption, risking essential fatty acid deficiency."
  },
  "Omega-6/Omega-3 Ratio": {
    description: "Compares omega-6 to omega-3 fats to show the balance of inflammatory signaling building blocks.",
    high: "Omega-6 fats heavily outweigh omega-3s, tilting the body toward a more inflammatory state.",
    low: "Omega-3 intake is strong relative to omega-6, a balance generally linked to better heart health."
  },
  "Arachidonic Acid/EPA Ratio": {
    description: "Compares the main pro-inflammatory omega-6 fat to the main anti-inflammatory omega-3 fat.",
    high: "Arachidonic acid dominates over EPA, favoring inflammation-promoting signals in the body.",
    low: "EPA is plentiful relative to arachidonic acid, usually reflecting strong omega-3 intake."
  },
  "EPA": {
    description: "Omega-3 fat from fish that helps calm inflammation and lower triglycerides.",
    high: "Usually reflects fish oil supplements or frequent fatty fish; very high levels may increase bleeding tendency.",
    low: "Too little of this anti-inflammatory fat, usually from eating little fatty fish."
  },
  "DHA": {
    description: "Omega-3 fat concentrated in the brain, eyes, and heart, obtained mainly from fish and algae.",
    high: "Usually reflects fish, fish oil, or algal oil intake and is rarely a concern on its own.",
    low: "Brain, eye, and heart cells may not be getting enough of this key structural fat."
  },
  "DPA": {
    description: "Omega-3 fat that bridges EPA and DHA and can be converted into either one.",
    high: "Typically reflects high intake of fish, seafood, or omega-3 supplements.",
    low: "Suggests low intake of fish and seafood omega-3s overall."
  },
  "Arachidonic Acid": {
    description: "Omega-6 fat from meat and eggs that the body uses to make inflammation and clotting signals.",
    high: "More raw material for inflammatory and clotting signals, especially when omega-3s are low.",
    low: "May reflect very high omega-3 intake, low fat intake, or poor fat absorption."
  },
  "Linoleic Acid": {
    description: "Essential omega-6 fat from nuts, seeds, and plant oils that the body cannot make itself.",
    high: "Reflects a diet rich in plant oils, nuts, and seeds; generally not harmful on its own.",
    low: "May signal essential fatty acid deficiency from very low fat intake or poor fat absorption."
  },
  "LDL Particle Number": {
    description: "Counts the actual number of LDL particles carrying cholesterol into artery walls.",
    high: "More cholesterol-carrying particles are available to enter artery walls and build plaque.",
    low: "Fewer artery-entering particles, which lowers long-term plaque and heart disease risk."
  },
  "LDL Small": {
    description: "Number of small, dense LDL particles, the type most likely to lodge in artery walls.",
    high: "Many small, dense LDL particles that slip into artery walls easily, often tied to insulin resistance.",
    low: "Few small, dense LDL particles, a favorable sign for artery health."
  },
  "LDL Medium": {
    description: "Number of medium-sized LDL particles, which still contribute to total artery-entering particle load.",
    high: "Adds to the total number of LDL particles that can build plaque in the arteries.",
    low: "Fewer medium LDL particles, generally a favorable finding."
  },
  "HDL Large": {
    description: "Number of large, cholesterol-rich HDL particles that help clear cholesterol from the arteries.",
    high: "Plenty of large HDL particles, usually a favorable sign of efficient cholesterol clearance.",
    low: "Fewer large HDL particles, often linked to insulin resistance, high triglycerides, and heart risk."
  },
  "LDL Peak Size": {
    description: "The most common size of your LDL particles, showing whether they are large or small and dense.",
    high: "LDL particles are mostly large and buoyant, a more favorable pattern for artery health.",
    low: "LDL particles are mostly small and dense, a pattern linked to insulin resistance and heart risk."
  },
  "LDL Pattern": {
    description: "Classifies LDL as Pattern A (mostly large particles) or Pattern B (mostly small, dense particles).",
    high: "Pattern B: mostly small, dense LDL particles, linked to insulin resistance and higher heart risk.",
    low: "Pattern A: mostly large, buoyant LDL particles, the more favorable pattern."
  },

  // --- Urinalysis ---
  "Urine Albumin": {
    description: "Measures small amounts of albumin protein leaking into urine, an early sign of kidney stress.",
    high: "Kidney filters are letting protein slip through, often an early sign of damage from diabetes or high blood pressure.",
    low: null
  },
  "Urine Appearance": {
    description: "Describes how clear or cloudy your urine looks, a quick screen for infection or crystals.",
    high: "Cloudy or turbid urine may point to infection, white blood cells, crystals, or mucus and should be checked against other urine results.",
    low: null
  },
  "Urine Color": {
    description: "Describes the color of your urine, which mainly reflects hydration, diet, and medications.",
    high: "Dark, red, brown, or unusual colors can mean dehydration, blood, liver-related pigments, or effects of foods and medications.",
    low: null
  },
  "Urine Specific Gravity": {
    description: "Measures how concentrated your urine is, reflecting hydration and the kidneys' ability to concentrate urine.",
    high: "Urine is very concentrated, most often from not drinking enough fluid or losing fluid through sweating, vomiting, or diarrhea.",
    low: "Urine is very dilute, from high fluid intake or, if persistent, a problem with the kidneys' ability to concentrate urine."
  },
  "Urine pH": {
    description: "Measures how acidic or alkaline your urine is, influenced by diet, metabolism, and kidney function.",
    high: "Alkaline urine can come from a plant-heavy diet, certain infections, or kidney acid-handling problems, and favors some stone types.",
    low: "Acidic urine can come from a high-protein diet, dehydration, or uncontrolled diabetes, and favors uric acid stones."
  },
  "Urine Protein": {
    description: "Screens for protein in urine, which healthy kidneys normally keep in the blood.",
    high: "Protein is leaking through the kidney filters, which can be temporary from exercise or illness or a sign of kidney disease.",
    low: null
  },
  "Urine Glucose": {
    description: "Checks for sugar spilling into urine, which normally does not happen at healthy blood sugar levels.",
    high: "Blood sugar is high enough to overflow into urine, most often from diabetes, or the kidneys are not reabsorbing sugar normally.",
    low: null
  },
  "Urine Ketones": {
    description: "Detects ketones in urine, produced when the body burns fat instead of sugar for fuel.",
    high: "Body is burning fat for fuel from fasting, low-carb eating, or illness, or, in diabetes, from a dangerous lack of insulin.",
    low: null
  },

  "Urine Bilirubin": {
    description: "Checks urine for bilirubin, a liver waste pigment that normally should not appear in urine.",
    high: "Bilirubin is spilling into urine, which can signal liver inflammation or a blocked bile duct.",
    low: null
  },
  "Urine Occult Blood": {
    description: "Dipstick screen for hidden blood (hemoglobin or myoglobin) in urine that you cannot see.",
    high: "Blood is present in urine, which can come from infection, stones, intense exercise, or kidney and bladder problems.",
    low: null
  },
  "Urine Nitrite": {
    description: "Screens for nitrite, a chemical made when certain bacteria grow in the urinary tract.",
    high: "Bacteria that convert nitrate to nitrite are likely present, suggesting a urinary tract infection.",
    low: null
  },
  "Urine Leukocyte Esterase": {
    description: "Detects an enzyme released by white blood cells, a sign of inflammation in the urinary tract.",
    high: "White blood cells are present in urine, most often from a urinary tract infection or inflammation.",
    low: null
  },
  "Urine RBC": {
    description: "Counts red blood cells seen under the microscope in a urine sample.",
    high: "More red blood cells than normal are in urine, which can come from infection, stones, exercise, or kidney and bladder conditions.",
    low: null
  },
  "Urine WBC": {
    description: "Counts white blood cells seen under the microscope in a urine sample.",
    high: "White blood cells in urine point to infection or inflammation somewhere in the urinary tract.",
    low: null
  },
  "Urine Bacteria": {
    description: "Checks whether bacteria are visible in the urine sample under the microscope.",
    high: "Bacteria are present, which may mean a urinary tract infection or contamination during collection.",
    low: null
  },
  "Urine Squamous Epithelial Cells": {
    description: "Counts skin-type cells in urine, mainly a check on how cleanly the sample was collected.",
    high: "Many skin cells suggest the sample was contaminated during collection, which can make other results less reliable.",
    low: null
  },
  "Urine Hyaline Casts": {
    description: "Counts clear protein cylinders formed in the kidney tubules and passed into urine.",
    high: "More casts than usual can follow dehydration or hard exercise, or may reflect kidney stress when persistent.",
    low: null
  }
};

/**
 * Look up marker description by normalized name.
 * Returns { description, high, low } or null if not found.
 */
export const getMarkerDescription = (normalizedName) => {
  if (!normalizedName || typeof normalizedName !== 'string') return null;
  return markerDescriptions[normalizedName] || null;
};

export default markerDescriptions;
